/**
 * Locale selection, loading and switching (03 §14.3, 06 §14.4).
 * - Detection: `user.locale` when signed in → the `locale` cookie → the best `navigator.languages` match → en.
 * - Switching loads the locale's strings and re-renders without a reload; it sets the cookie for a year and, when
 *   signed in, `user.locale`. `<html lang>` follows (the runtime sets it).
 */

import { loaders } from 'virtual:tarock-i18n';
import { effect, signal } from '@preact/signals';
import { getLocale, initI18n, isLocale, type Locale, onLocaleChange, pickLocale, setLocale } from '@tarock/i18n';
import { api } from '../net/api';
import { authStore } from '../stores/auth';

/** The active locale, for components that must re-render on a switch (the app root reads it). */
export const localeSignal = signal<Locale>('en');

const COOKIE_MAX_AGE_S = 365 * 24 * 3600;

/** The `locale` cookie set by the switcher, if any. */
function cookieLocale(): Locale | null {
  const m = /(?:^|;\s*)locale=([^;]*)/.exec(document.cookie);
  const v = m?.[1] === undefined ? null : decodeURIComponent(m[1]);
  return isLocale(v) ? v : null;
}

function setCookie(locale: Locale): void {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is missing in Firefox and older Safari
  document.cookie = `locale=${locale}; Max-Age=${COOKIE_MAX_AGE_S}; Path=/; SameSite=Lax${secure}`;
}

/**
 * The switcher's action: loads `locale`, remembers it in the cookie and, for a signed-in user (not while an admin
 * views as them), in `user.locale`.
 */
export async function switchLocale(locale: Locale): Promise<void> {
  setCookie(locale);
  await setLocale(locale);
  const user = authStore.user.peek();
  if (user && !user.impersonatedBy && user.locale !== locale) {
    const r = await api('/api/profile', { method: 'PATCH', body: { locale } });
    if (r.ok) {
      authStore.user.value = { ...user, locale };
    }
  }
}

/** A signed-in user's `user.locale` comes first (03 §14.3): applied after each auth probe. */
function followUserLocale(): void {
  effect(() => {
    const user = authStore.user.value;
    if (user && !user.impersonatedBy && isLocale(user.locale) && user.locale !== getLocale()) {
      void setLocale(user.locale);
    }
  });
}

/** Detects the locale and loads its strings; then keeps the signal and `user.locale` in step. Await before rendering. */
export async function initLocale(): Promise<Locale> {
  onLocaleChange((l) => {
    localeSignal.value = l;
  });
  const locale = await initI18n({
    loaders,
    locale: pickLocale({ cookie: cookieLocale(), languages: navigator.languages }),
  });
  localeSignal.value = locale;
  followUserLocale();
  return locale;
}
